import { DataTable } from './DataVis/DataTable';
import DownloadButton from './Button/DownloadButton';
import Stack from './Stack';
import { TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import { TIKTOK_SORT_OPTIONS } from '../../utils/metrics';
import TableHeading from './TableHeading';
import { createDefaultColumns } from './DataVis/DataTable/utils';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import { getCreatorHealthStats } from '../../state/utils';
import styled from 'styled-components';
import { useCreators } from 'utils/hooks/api';
import {
    useSortSearchParamsStateOnly,
    useSortStateOnly,
} from 'utils/hooks/useSort';
import useTableColumns from 'utils/hooks/useTableColumns';

const SectionHeading = styled.div`
    margin: 24px 0;
    font-size: 18px;
    font-weight: 600;
    display: flex;
    flex-wrap: wrap;
`;

const creatorFormatCell = ({ row }) => {
    return (
        <a
            href={row.url}
            target="_blank"
            css={`
                color: ${({ theme }) => theme.purple_1};
            `}
        >
            {row.creator}
        </a>
    );
};

const creatorsMetrics = [
    [TIKTOK_METADATA_BY_KEY.nb_likes_all, true],
    [TIKTOK_METADATA_BY_KEY.nb_likes_last_week, false],
    [TIKTOK_METADATA_BY_KEY.nb_views_all, true],
    [TIKTOK_METADATA_BY_KEY.nb_views_last_week, false],
    [TIKTOK_METADATA_BY_KEY.paid_posts_views_prc_all, true],
    [TIKTOK_METADATA_BY_KEY.engagement_prc_all, true],
    [TIKTOK_METADATA_BY_KEY.eligible_for_commission_posts_views_prc_all, false],
];

const rankTypeSortMap = {
    last_week_likes: TIKTOK_SORT_OPTIONS.nb_likes_last_week,
    total_likes: TIKTOK_SORT_OPTIONS.nb_likes_all,
    last_week_views: TIKTOK_SORT_OPTIONS.nb_views_last_week,
    total_views: TIKTOK_SORT_OPTIONS.nb_views_all,
    total_paid_posts: TIKTOK_SORT_OPTIONS.paid_posts_views_prc_all,
    total_eligible_for_commission:
        TIKTOK_SORT_OPTIONS.eligible_for_commission_posts_views_prc_all,
    engagement: TIKTOK_SORT_OPTIONS.engagement_prc_all,
};

const sortMetricMap = Object.entries(rankTypeSortMap).reduce(
    (acc, [key, { sortBy }]) => {
        acc[sortBy] = key;
        return acc;
    },
    {},
);

const DEFAULT_RANK_TYPE = 'total_likes';
const DEFAULT_SORT_OPTION = rankTypeSortMap[DEFAULT_RANK_TYPE];
const creatorSortOptions = Object.values(rankTypeSortMap);

// The assistant's "View all" link encodes the creators sort as the raw metric key
// (`?creatorSort=<sortBy>`), so the accessor keys off sortBy for deep-link round-trips.
const creatorSortOptionKeyAccessor = ({ sortBy }) => sortBy;

// Creators sort state, built on the shared useSort hooks. Section tables (given a
// `sortParamKey`) sync the sort to that deep-linkable URL param — matching the
// assistant's "View all" link and the sibling tab tables; the drawer variant keeps it
// local. Both hooks are always evaluated to satisfy the rules of hooks; only the
// selected result drives the table.
const useCreatorSortOption = (sortParamKey) => {
    const localSort = useSortStateOnly(DEFAULT_SORT_OPTION);
    const urlSort = useSortSearchParamsStateOnly({
        initialSortOption: DEFAULT_SORT_OPTION,
        options: creatorSortOptions,
        sortOptionKeyAccessor: creatorSortOptionKeyAccessor,
        paramKey: sortParamKey,
    });
    return sortParamKey ? urlSort : localSort;
};

const useCreatorsTableProps = ({
    year,
    week,
    category,
    brand,
    hashtag,
    country,
    sortParamKey,
}) => {
    const { sortOption, setSortOption: persistSortOption } =
        useCreatorSortOption(sortParamKey);
    const rankType = sortMetricMap[sortOption.sortBy];
    if (!rankType) {
        throw new Error('Unknown rank type');
    }

    const { visibleOrderedMetrics, columnsMenu, setSortOption } =
        useTableColumns({
            tableName: 'creators-table',
            metrics: creatorsMetrics,
            setSortOption: persistSortOption,
            // Passing the active sort lets useTableColumns reveal that column when it's
            // hidden — on mount (e.g. a URL deep-link sorting by a hidden column) and on
            // manual sort changes (via its extendedSetSortOption).
            sortOption,
        });
    const creatorsQuery = useCreators({
        year,
        week,
        categories: category ? [category] : null,
        brands: brand ? [brand] : null,
        hashtags: hashtag ? [hashtag] : null,
        countries: country ? [country] : null,
        rank_type: rankType,
        orderBy:
            sortOption?.sortBy === 'engagement_prc_all'
                ? 'engagement_prc'
                : sortOption?.sortBy,
    });
    const dataReady =
        creatorsQuery.isSuccess && creatorsQuery.data?.data?.length > 0;
    const data =
        creatorsQuery.data?.data?.map(({ url, data }) => ({
            id: url,
            creator: `@${url.split('@')[1]}`,
            url,
            nb_likes_last_week: data?.nb_likes_last_week,
            nb_likes_all: data?.nb_likes_all,
            nb_views_last_week: data?.nb_views_last_week,
            nb_views_all: data?.nb_views_all,
            engagement_prc_all: data?.engagement_prc,
            paid_posts_views_prc_all: data?.paid_posts_views_prc_all,
            eligible_for_commission_posts_views_prc_all:
                data?.eligible_for_commission_posts_views_prc_all,
            healthStats: getCreatorHealthStats(data),
        })) || null;

    const columns = [
        {
            key: 'creator',
            $minWidth: 15,
            $width: '45%',
            formatCell: creatorFormatCell,
            sortable: false,
        },
        ...createDefaultColumns(visibleOrderedMetrics, {
            showHealthStatLabels: true,
        }),
    ];

    const drawerColumns = [
        {
            key: 'creator',
            $minWidth: 12,
            $width: '45%',
            formatCell: creatorFormatCell,
            sortable: false,
        },
        ...createDefaultColumns(visibleOrderedMetrics, {
            showHealthStatLabels: true,
        }).map((column) => ({
            ...column,
            $minWidth: 8,
        })),
    ];

    return {
        data,
        dataReady,
        sortOption,
        setSortOption,
        creatorsQuery,
        columnsMenu,
        columns,
        drawerColumns,
    };
};

const creatorCSVColumns = [
    { key: 'creator', label: 'Creator' },
    { key: 'url', label: 'Profile' },
    ...creatorsMetrics.map(([metric]) => ({
        key: metric.key,
        label: metric.tableHeadColumnLabel,
    })),
];

const downloadCreatorsDataAsCSV = (filename, data) => {
    downloadItemsAsCSV(filename, data, creatorCSVColumns);
};

const CreatorsSectionTable = ({
    year,
    week,
    category,
    brand,
    hashtag,
    country,
    filename,
    name,
    sortParamKey = 'creatorSort',
}) => {
    const {
        data,
        dataReady,
        sortOption,
        setSortOption,
        creatorsQuery,
        columnsMenu,
        columns,
    } = useCreatorsTableProps({
        year,
        week,
        category,
        brand,
        hashtag,
        country,
        sortParamKey,
    });

    return (
        <>
            <TableHeading
                title="Creators"
                subtitle={
                    <>
                        Who are the top creators in{' '}
                        <span css="text-transform: capitalize;">{name}</span>?
                    </>
                }
                tooltipKey="creatorsTitleHelp"
                tools={
                    <DownloadButton
                        isDisabled={!dataReady}
                        isLoading={creatorsQuery.isLoading}
                        onClick={() => {
                            downloadCreatorsDataAsCSV(filename, data);
                        }}
                        mixpanelMeta={{ contentKey: 'CreatorsTable' }}
                    />
                }
            />
            <Stack
                css={`
                    padding: 1.25rem;
                `}
            >
                <div
                    css={`
                        display: flex;
                        justify-content: flex-end;
                        padding-bottom: 0.75rem;
                    `}
                >
                    {columnsMenu}
                </div>
                <DataTable
                    data={data?.map((row) => ({
                        ...row,
                        fetching: false,
                        fetchingData: false,
                    }))}
                    week={week}
                    year={year}
                    columns={columns}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    noDataMessage="Creator data is not available for this entity."
                    onRowClick={false}
                />
            </Stack>
        </>
    );
};

export const CreatorsTable = ({
    year,
    week,
    category,
    brand,
    hashtag,
    country,
    filename,
}) => {
    const {
        data,
        dataReady,
        creatorsQuery,
        sortOption,
        setSortOption,
        columnsMenu,
        drawerColumns,
    } = useCreatorsTableProps({
        year,
        week,
        category,
        brand,
        hashtag,
        country,
    });

    return (
        <>
            <SectionHeading>
                <span css="display: flex; align-items: center;">
                    Top Creators
                </span>
                <DownloadButton
                    isDisabled={!dataReady}
                    isLoading={creatorsQuery.isLoading}
                    onClick={() => {
                        downloadCreatorsDataAsCSV(filename, data);
                    }}
                    mixpanelMeta={{ contentKey: 'CreatorsTable' }}
                    css={`
                        margin-left: auto;
                    `}
                />
            </SectionHeading>
            <div
                css={`
                    display: flex;
                    justify-content: flex-end;
                    margin-bottom: 0.75rem;
                `}
            >
                {columnsMenu}
            </div>
            <div
                css={`
                    font-size: 0.8rem;
                `}
            >
                <DataTable
                    data={data}
                    week={week}
                    year={year}
                    columns={drawerColumns}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    noDataMessage="Creator data is not available for this entity."
                    onRowClick={false}
                    $maxHeight="18.5rem"
                />
            </div>
        </>
    );
};

export default CreatorsSectionTable;
